import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Button,
  MenuItem,
  TextField,
  Link,
  Pagination,
} from '@mui/material';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { useClient } from '../ClientContext';
import {
  formatLoginDate,
  getOutcomeColor,
  OUTCOME_FILTERS,
  buildDashboardFilters,
  buildDomainFilters,
  getDashboardDisplayName,
  getAuth0UserProfileUrl,
} from '../utils/loginHistory';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const LoginHistoryPanel = ({ accessToken }) => {
  const { config } = useClient();
  const [entries, setEntries] = useState([]);
  const [outcome, setOutcome] = useState(OUTCOME_FILTERS[0].value);
  const [tenantKey, setTenantKey] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const dashboardFilters = useMemo(
    () => buildDashboardFilters(config?.loginHistoryTenants || []),
    [config?.loginHistoryTenants],
  );
  const domainFilters = useMemo(
    () => buildDomainFilters(config?.loginHistoryDomains || []),
    [config?.loginHistoryDomains],
  );
  const tenants = config?.loginHistoryTenants || [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPage = useCallback(
    async (pageNumber) => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        page: String(pageNumber),
      });
      if (outcome) params.set('outcome', outcome);
      if (tenantKey) params.set('tenantKey', tenantKey);
      if (emailDomain) params.set('emailDomain', emailDomain);
      if (search) params.set('search', search);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/login-history?${params}`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch login history');
      }

      return response.json();
    },
    [accessToken, outcome, tenantKey, emailDomain, search],
  );

  const loadPage = useCallback(
    async (pageNumber, isRefresh = false) => {
      if (!accessToken) return;

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const data = await fetchPage(pageNumber);
        setEntries(data.loginHistory || []);
        setTotal(typeof data.total === 'number' ? data.total : 0);
        setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0);
        setPage(typeof data.page === 'number' ? data.page : pageNumber);
        setError(null);
      } catch (err) {
        console.error('Error fetching login history:', err);
        setError('Could not load login history. The list below may be incomplete.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accessToken, fetchPage],
  );

  useEffect(() => {
    loadPage(page);
  }, [loadPage, page]);

  const showEmptyState = !loading && !error && entries.length === 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            size="small"
            label="Search name or email"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ minWidth: 240 }}
          />
          <TextField
            select
            size="small"
            label="Outcome"
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 220 }}
          >
            {OUTCOME_FILTERS.map((filter) => (
              <MenuItem key={filter.label} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Dashboard"
            value={tenantKey}
            onChange={(event) => {
              setTenantKey(event.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 200 }}
          >
            {dashboardFilters.map((filter) => (
              <MenuItem key={filter.label} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Email domain"
            value={emailDomain}
            onChange={(event) => {
              setEmailDomain(event.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 200 }}
          >
            {domainFilters.map((filter) => (
              <MenuItem key={filter.label} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => loadPage(page, true)} disabled={refreshing || loading} size="small">
            {refreshing ? <CircularProgress size={18} /> : <RefreshCw size={18} />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => loadPage(page, true)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : showEmptyState ? (
        <Typography variant="body2">No login attempts recorded for this filter.</Typography>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Connection</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Dashboard</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Auth0</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const auth0Url = getAuth0UserProfileUrl(entry.auth0Id);
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>{formatLoginDate(entry.createdAt)}</TableCell>
                      <TableCell>{entry.userName || '—'}</TableCell>
                      <TableCell>{entry.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={entry.outcome}
                          size="small"
                          sx={{
                            bgcolor: `${getOutcomeColor(entry.outcome)}20`,
                            color: getOutcomeColor(entry.outcome),
                            fontWeight: 'bold',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>{entry.connection || '—'}</TableCell>
                      <TableCell>{entry.roleAtLogin || '—'}</TableCell>
                      <TableCell>{getDashboardDisplayName(entry.tenantKey, tenants)}</TableCell>
                      <TableCell>{entry.ip || '—'}</TableCell>
                      <TableCell>{entry.failureReason || '—'}</TableCell>
                      <TableCell align="right">
                        {auth0Url ? (
                          <Tooltip title="Open user in Auth0">
                            <IconButton
                              component={Link}
                              href={auth0Url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              aria-label={`Open Auth0 profile for ${entry.email}`}
                            >
                              <ExternalLink size={16} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {total} {total === 1 ? 'result' : 'results'}
            </Typography>
            {totalPages > 1 && (
              <Pagination
                color="primary"
                count={totalPages}
                page={page}
                onChange={(_event, nextPage) => setPage(nextPage)}
                showFirstButton
                showLastButton
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default LoginHistoryPanel;
